import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Seo } from "../lib/seo";
import { MaskedLines, Reveal } from "../lib/motion";

const ROWS = [
  {
    name: "The Perfect Break",
    us: true,
    what: "Adult short breaks in the Heart of England: hand-picked hotel + breakfast + three experiences you choose",
    pricing: "From £169 for two people — the price covers both of you",
    experiences: "Three included, chosen by you from a local collection",
    booking: "Pick dates (flexible OK) → see available hotels → pick experiences → done",
  },
  {
    name: "Warner Hotels",
    what: "Adults-only hotel resorts with dining and entertainment on-site",
    pricing: "Typically higher-priced, per-room packages with dinner included",
    experiences: "On-site entertainment rather than local experiences you choose",
    booking: "Direct on their own site",
  },
  {
    name: "Superbreak",
    what: "Deal marketplace for UK and overseas breaks (Wowcher-owned)",
    pricing: "Per-person 'from' deals with strikethrough pricing",
    experiences: "Hotel-led; extras vary deal by deal",
    booking: "Marketplace checkout",
  },
  {
    name: "Secret Escapes",
    what: "Members-only flash-sale travel club",
    pricing: "Time-limited member prices behind a signup wall",
    experiences: "Hotel-led luxury deals; experiences not included as standard",
    booking: "Requires free membership to see prices",
  },
  {
    name: "Great Little Breaks",
    what: "Established UK packaged-break operator",
    pricing: "Per-person 'from' pricing with save-up-to badges",
    experiences: "Fixed extras (e.g. dinner, tickets) rather than pick-any-three",
    booking: "Direct on their own site",
  },
];

export default function Compare() {
  return (
    <main data-testid="compare-page">
      <Seo
        title="Considering Warner Hotels or Superbreak? | How The Perfect Break Compares"
        description="Looking at Warner Hotels, Superbreak, Secret Escapes or Great Little Breaks? See how The Perfect Break compares: hand-picked hotel, breakfast and three experiences you choose — from £169 for two people, not per person."
        path="/compare"
      />
      <section className="bg-primary py-20 text-sand sm:py-28">
        <div className="container-tpb">
          <p className="overline !text-sand/80">An honest comparison</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight sm:text-6xl" data-testid="compare-heading">
            <MaskedLines immediate lines={["Shopping around?", { text: "Good. So did we.", className: "italic" }]} />
          </h1>
          <p className="mt-5 max-w-xl leading-relaxed text-sand/80" data-testid="compare-intro">
            If you've been looking at Warner Hotels, Superbreak, Secret Escapes or Great Little Breaks, here's where we genuinely fit — and where we don't. Based on each operator's public positioning, August 2026.
          </p>
        </div>
      </section>

      <section className="container-tpb py-16 sm:py-24">
        <div className="space-y-6">
          {ROWS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.06}>
              <div className={`rounded-lg border p-7 sm:p-9 ${r.us ? "border-primary bg-primary text-sand" : "border-sandline bg-white"}`} data-testid={`compare-row-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className={`font-serif text-2xl font-semibold sm:text-3xl ${r.us ? "text-sand" : "text-ink"}`}>{r.name}</h2>
                  {r.us && <span className="rounded-full bg-accent px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white">That's us</span>}
                </div>
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {[["What they offer", r.what], ["Pricing", r.pricing], ["Experiences", r.experiences], ["How booking works", r.booking]].map(([label, value]) => (
                    <div key={label}>
                      <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${r.us ? "text-sand/60" : "text-ink-muted"}`}>{label}</p>
                      <p className={`mt-2 text-sm leading-relaxed ${r.us ? "text-sand/90" : "text-ink"}`}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 grid gap-8 rounded-lg border border-sandline bg-sand-dark p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <h2 className="font-serif text-3xl font-medium text-primary">Where we win</h2>
              <ul className="mt-5 space-y-3">
                {[
                  "The price you see is for two people — never per person",
                  "Three experiences included, and you choose which three",
                  "No membership wall, no countdown timers, no gimmicks",
                  "Grown-up breaks without grown-up-resort prices",
                ].map((w) => (
                  <li key={w} className="flex items-start gap-3 text-ink">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10"><Check className="h-3 w-3 text-primary" /></span>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-serif text-3xl font-medium text-primary">Where we don't (yet)</h2>
              <ul className="mt-5 space-y-3">
                {[
                  "We cover the Heart of England — not the whole UK (yet)",
                  "No on-site spa resorts or evening entertainment like Warner",
                  "A smaller collection — two hand-picked breaks, done properly",
                ].map((w) => (
                  <li key={w} className="flex items-start gap-3 text-ink-muted">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink/5"><Minus className="h-3 w-3" /></span>
                    {w}
                  </li>
                ))}
              </ul>
              <Link to="/breaks" className="btn-accent group mt-8" data-testid="compare-cta">
                See our breaks <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
